import React, { useMemo, useState } from "react";
import { Col, Progress, Row, Tag } from "antd";
import {
  CloudServerOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  ApiOutlined,
  BarChartOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { FiRefreshCw } from "react-icons/fi";
import CommonCard from "../../../../global/card";
import CommonTable from "../../../../global/table";
import CommonSkeletonLoader from "../../../../global/loading";
import NoRecordFound from "../../../../global/noRecordFound";
import { QUERY_KEYS } from "../../../../../lib/query-constants";
import { useGenericQuery } from "../../../../../queries/use-generic-query";
import { formatDateTime, formatNumber } from "../../../../../utils/functions";
import { cn } from "../../../../../utils/cn";

const RANGE_OPTIONS = [
  { label: "Last 7 days", value: "7" },
  { label: "Last 14 days", value: "14" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
];

const PRIMARY = "var(--primary-color, #1890ff)";

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / 1024 ** index;
  return `${scaled.toFixed(scaled >= 10 || index === 0 ? 0 : 2)} ${units[index]}`;
};

const StatCard = ({ title, value, sub, icon, iconClassName = "text-primary", iconBgClassName = "bg-blue-50" }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 h-full">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-500 mb-1">{title}</p>
        <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
        {sub ? <p className="text-xs text-gray-400 mt-1 leading-relaxed">{sub}</p> : null}
      </div>
      <div
        className={cn(
          "w-9 h-9 rounded-lg flex items-center justify-center text-base shrink-0",
          iconBgClassName,
          iconClassName,
        )}
      >
        {icon}
      </div>
    </div>
  </div>
);

const SectionHeading = ({ title, icon, subtitle }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2">
      <span style={{ color: PRIMARY, fontSize: 16 }}>{icon}</span>
      <h3 className="text-base font-semibold text-gray-900 m-0">{title}</h3>
    </div>
    {subtitle ? <p className="text-xs text-gray-500 mt-1 mb-0 ml-6">{subtitle}</p> : null}
  </div>
);

const EnhancedWebshareProxyStatistics = () => {
  const [days, setDays] = useState("7");

  const queryParams = useMemo(() => ({ days }), [days]);

  const { data, isLoading, isFetching, refetch } = useGenericQuery({
    endpoint: "admin/proxies/webshare/statistics",
    queryKey: QUERY_KEYS.webshareProxyStatistics.lists(queryParams),
    queryParams,
  });

  const hourlyColumns = [
    {
      title: "Hour",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Bandwidth",
      dataIndex: "bandwidth_total",
      key: "bandwidth_total",
      render: (value) => formatBytes(value),
    },
    {
      title: "Requests",
      dataIndex: "requests_total",
      key: "requests_total",
      render: (value) => formatNumber(value || 0),
    },
    {
      title: "Success",
      dataIndex: "requests_successful",
      key: "requests_successful",
      render: (value) => formatNumber(value || 0),
    },
    {
      title: "Failed",
      dataIndex: "requests_failed",
      key: "requests_failed",
      render: (value) => formatNumber(value || 0),
    },
    {
      title: "Proxies used",
      dataIndex: "number_of_proxies_used",
      key: "number_of_proxies_used",
      render: (value) => formatNumber(value || 0),
    },
  ];

  const errorColumns = [
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (text) => <span className="font-mono text-xs">{text}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => <Tag className="capitalize m-0">{text || "-"}</Tag>,
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      render: (value) => formatNumber(value || 0),
    },
    {
      title: "How to fix",
      dataIndex: "how_to_fix",
      key: "how_to_fix",
      render: (text) => <span className="text-sm text-gray-600">{text || "-"}</span>,
    },
  ];

  const hourlyData = (data?.hourlyStats || []).map((row, index) => ({
    ...row,
    key: row.timestamp || index,
  }));

  const errorData = (data?.breakdown?.errorReasons || []).map((row, index) => ({
    ...row,
    key: row.reason || index,
  }));

  const countryEntries = Object.entries(data?.breakdown?.countriesUsed || {}).sort(
    (a, b) => b[1] - a[1],
  );
  const protocolEntries = Object.entries(data?.breakdown?.protocolsUsed || {}).sort(
    (a, b) => b[1] - a[1],
  );

  const bandwidth = data?.bandwidth || {};
  const requests = data?.requests || {};
  const plan = data?.plan || {};
  const subscription = data?.subscription || {};

  const billingSubtitle =
    subscription.startDate && subscription.endDate
      ? `Billing period ${formatDateTime(subscription.startDate)} → ${formatDateTime(subscription.endDate)}`
      : "Current billing period";

  const planLabel = [
    plan.proxyType,
    plan.proxySubtype,
    plan.proxyCount ? `${formatNumber(plan.proxyCount)} proxies` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <CommonCard
      isFilters={{
        show: true,
        data: [
          {
            name: "statsRange",
            value: days,
            placeholder: "Time range",
            options: RANGE_OPTIONS,
            onChange: (value) => setDays(value || "7"),
            className: "w-full sm:!w-44",
          },
        ],
      }}
      isExtraButton={{
        show: true,
        data: [
          {
            title: "Refresh",
            icon: <FiRefreshCw size={16} />,
            onClick: () => refetch(),
            className:
              "h-10 !bg-transparent !border !border-primary !text-primary hover:!bg-primary hover:!text-white",
          },
        ],
      }}
      isLoading={{
        show: isLoading,
        loaderCmp: <CommonSkeletonLoader type="stats" />,
      }}
      className={!isLoading && isFetching ? "opacity-60 pointer-events-none" : ""}
    >
      {!data && !isLoading ? (
        <NoRecordFound message="Unable to load Webshare statistics." />
      ) : (
        <div className="space-y-8">
          <section>
            <SectionHeading
              title="Bandwidth usage"
              icon={<ThunderboltOutlined />}
              subtitle={
                <>
                  {billingSubtitle}
                  {subscription.throttled ? (
                    <Tag color="orange" className="ml-2 m-0 align-middle">
                      Throttled
                    </Tag>
                  ) : null}
                  {planLabel ? (
                    <span className="ml-2 capitalize">· Plan: {planLabel}</span>
                  ) : null}
                </>
              }
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <StatCard
                title="Used (billing period)"
                value={formatBytes(bandwidth.usedBytes)}
                sub={`Projected ${formatBytes(bandwidth.projectedBytes)} · ${formatBytes(bandwidth.rangeUsedBytes || 0)} in selected range`}
                icon={<ThunderboltOutlined />}
              />
              <StatCard
                title="Remaining"
                value={bandwidth.isUnlimited ? "Unlimited" : formatBytes(bandwidth.remainingBytes)}
                sub={
                  bandwidth.isUnlimited
                    ? "No bandwidth cap on this plan"
                    : `Limit ${plan.bandwidthLimitGb ?? "-"} GB`
                }
                icon={<CloudServerOutlined />}
                iconBgClassName="bg-slate-50"
                iconClassName="text-slate-600"
              />
              <StatCard
                title="Success rate"
                value={requests.successRate != null ? `${requests.successRate}%` : "-"}
                sub={`${formatNumber(requests.successful || 0)} / ${formatNumber(requests.total || 0)} requests in range`}
                icon={<CheckCircleOutlined />}
                iconBgClassName="bg-green-50"
                iconClassName="text-green-600"
              />
            </div>

            {!bandwidth.isUnlimited && bandwidth.usedPercent != null ? (
              <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
                <div className="flex justify-between text-xs font-medium text-gray-500 mb-2">
                  <span>{bandwidth.usedPercent}% used this billing period</span>
                  <span>{formatBytes(bandwidth.remainingBytes)} left</span>
                </div>
                <Progress
                  percent={bandwidth.usedPercent}
                  showInfo={false}
                  strokeColor={
                    bandwidth.usedPercent >= 90
                      ? "#ef4444"
                      : bandwidth.usedPercent >= 75
                        ? "#f97316"
                        : PRIMARY
                  }
                  trailColor="#e2e8f0"
                  size={["100%", 8]}
                />
              </div>
            ) : null}
          </section>

          <section>
            <SectionHeading
              title="Request activity"
              icon={<ApiOutlined />}
              subtitle={`Counts for the selected ${days}-day range`}
            />
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Total requests"
                  value={formatNumber(requests.total || 0)}
                  icon={<ApiOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Successful"
                  value={formatNumber(requests.successful || 0)}
                  icon={<CheckCircleOutlined />}
                  iconBgClassName="bg-green-50"
                  iconClassName="text-green-600"
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Failed"
                  value={formatNumber(requests.failed || 0)}
                  icon={<CloseCircleOutlined />}
                  iconBgClassName="bg-red-50"
                  iconClassName="text-red-500"
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Unique proxies used"
                  value={formatNumber(requests.proxiesUsed || 0)}
                  sub={
                    requests.lastRequestSentAt
                      ? `Last request ${formatDateTime(requests.lastRequestSentAt)}`
                      : null
                  }
                  icon={<GlobalOutlined />}
                  iconBgClassName="bg-violet-50"
                  iconClassName="text-violet-600"
                />
              </Col>
            </Row>
          </section>

          <section>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <SectionHeading title="Countries used" icon={<GlobalOutlined />} />
                {countryEntries.length ? (
                  <div className="flex flex-wrap gap-2">
                    {countryEntries.map(([code, count]) => (
                      <Tag key={code} className="m-0">
                        {code}: {formatNumber(count)}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-0">No country data in this range.</p>
                )}
              </Col>
              <Col xs={24} lg={12}>
                <SectionHeading title="Protocols used" icon={<BarChartOutlined />} />
                {protocolEntries.length ? (
                  <div className="flex flex-wrap gap-2">
                    {protocolEntries.map(([protocol, count]) => (
                      <Tag key={protocol} className="m-0 uppercase">
                        {protocol}: {formatNumber(count)}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-0">No protocol data in this range.</p>
                )}
              </Col>
            </Row>
          </section>

          <section>
            <SectionHeading
              title="Hourly usage"
              icon={<BarChartOutlined />}
              subtitle={`${data?.breakdown?.hourlyWithTraffic || 0} hours with traffic in selected range`}
            />
            {hourlyData.length ? (
              <CommonTable columns={hourlyColumns} data={hourlyData} pagination={{ pageSize: 10 }} />
            ) : (
              <NoRecordFound message="No hourly stats for this period." />
            )}
          </section>

          <section>
            <SectionHeading title="Error breakdown" icon={<WarningOutlined />} />
            {errorData.length ? (
              <CommonTable columns={errorColumns} data={errorData} pagination={false} />
            ) : (
              <NoRecordFound message="No failed request errors in this period." />
            )}
          </section>
        </div>
      )}
    </CommonCard>
  );
};

export default EnhancedWebshareProxyStatistics;
